// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useCallback, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PrivacyPolicy } from '../components/privacy/PrivacyPolicy';
import { useToast } from '../components/ui/toast';
import { cn } from '../lib/utils';
import { registrationSchema, RegistrationFormData, getErrorMessage } from '../lib/validations';
import { createRegistration } from '../lib/api';
import { ExhibitionType } from '../types';
import { sendRegistrationCompleteNotification } from '../lib/notification';
import EnvDebug from '../components/debug/EnvDebug';
import { Helmet } from 'react-helmet';

// 전시회별 브랜딩 정보
const EXHIBITION_BRANDING = {
  urbanbreak: {
    name: 'UrbanBreak',
    title: '현장등록 (On-site Registration)',
    logo: '/logo_420_user.png',
    backgroundImage: '/urban_bg.png',
    primaryColor: 'bg-blue-600',
    description: '',
    smsConsentText: (
      <>
        차기 전시회 관련 안내 문자 수신 동의<br />
        <span className="text-gray-500">(I agree to receive SMS notifications for future exhibitions)</span> (선택)
      </>
    ),
    buttonText: 'UrbanBreak 현장등록',
    ticketName: 'UrbanBreak 일반 입장권', // 티켓명 추가
    // Open Graph 메타 데이터
    ogImage: '/og_urban.jpg',
    ogTitle: 'UrbanBreak 2025',
    ogDescription: '아시아 최대 어반&스트릿 아트페어 어반브레이크. 세상에서 가장 재미있는 아트 페스티벌을 만들어 갑니다.',
    // Favicon
    favicon: '/icon_urban.png',
  },
  toycon: {
    name: 'TOY CON SEOUL',  
    title: '현장등록 (On-site Registration)',
    logo: '/logo_toycon.png',
    backgroundImage: '/toycon_bg.jpeg',
    primaryColor: 'bg-red-600',
    description: '',
    smsConsentText: (
      <>
        차기 전시회 관련 안내 문자 수신 동의<br />
        <span className="text-gray-500">(I agree to receive SMS notifications for future exhibitions)</span> (선택)
      </>
    ),
    buttonText: 'TOY CON SEOUL 현장등록',
    ticketName: 'TOY CON SEOUL 일반 입장권', // 티켓명 추가
    // Open Graph 메타 데이터
    ogImage: '/og_toycon.jpg',
    ogTitle: 'TOY CON SEOUL 2025',
    ogDescription: '아트토이와 일상이 하나되는 순간, 토이콘 서울에 함께해주셔서 감사합니다. 전 세계 10개국 100여 명의 아티스트, 브랜드, 컬렉터들이 참여하는 국내 유일·최대 규모의 컬렉터블 토이 페어입니다.',
    // Favicon
    favicon: '/icon_toycon.png',
  },
} as const;

interface RegistrationPageProps {
  exhibitionType: ExhibitionType;
}

/**
 * 사전등록 페이지
 * 전시회 관람객이 사전등록을 진행하는 페이지입니다.
 */
const RegistrationPage: React.FC<RegistrationPageProps> = ({ exhibitionType }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  
  // 현재 전시회 브랜딩 정보
  const branding = EXHIBITION_BRANDING[exhibitionType];

  // 디버깅을 위한 로그
  console.log('🎨 [RegistrationPage] 현재 전시회:', exhibitionType);
  console.log('🖼️ [RegistrationPage] 배경 이미지:', branding.backgroundImage);
  console.log('🎯 [RegistrationPage] 브랜딩 정보:', branding);

  // 배경 이미지 로드 확인
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      console.log('✅ [RegistrationPage] 배경 이미지 로드 성공:', branding.backgroundImage);
    };
    img.onerror = () => {
      console.error('❌ [RegistrationPage] 배경 이미지 로드 실패:', branding.backgroundImage);
    };
    img.src = branding.backgroundImage;
  }, [branding.backgroundImage]);

  // React Hook Form 설정
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof registrationSchema>>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      nationality: 'domestic',
      gender: 'male',
      age_group: 'twenties',
      region: 'seoul',
      privacy_consent: false,
      third_party_consent: false,
      marketing_consent: false,
      sms_consent: false,
      exhibition_type: exhibitionType, // 전시회 타입 설정
    },
  });

  // 폼 제출 핸들러
  const onSubmit: SubmitHandler<RegistrationFormData> = async (data) => {
    setIsLoading(true);
    
    try {
      // 실제 API 호출 - Supabase에 데이터 저장
      const result = await createRegistration({
        ...data,
        exhibition_type: exhibitionType,
      });
      
      console.log('등록 성공:', result);
      showToast(`${branding.name} 현장등록이 완료되었습니다! 알림톡을 확인해 주세요.`, 'success');
      
      // 등록 성공 후 알림톡 발송
      try {
        const alimtalkResult = await sendRegistrationCompleteNotification({
          name: data.name,
          phone: data.phone || '',
          registrationId: result.id || '',
          registrationCode: result.registration_id || '',
          exhibitionType: exhibitionType,
          ticket_name: branding.ticketName,
        });
        console.log('✅ 알림톡 발송 성공');
        console.log('📨 알림톡 발송 응답 상세:', JSON.stringify(alimtalkResult, null, 2));
      } catch (notificationError) {
        console.error('❌ 알림톡 발송 실패:', notificationError);
        console.error('❌ 알림톡 오류 상세:', JSON.stringify(notificationError, null, 2));
        // 알림톡 실패해도 등록은 성공이므로 계속 진행
      }

      // 완료 페이지로 이동 (등록 데이터를 state로 전달)
      navigate(`/complete/${result.id}`, {
        state: {
          exhibitionType,
          registrationData: {
            ...result,
            email: data.email,
            nationality: data.nationality,
            exhibition_type: exhibitionType,
          }
        }
      });
    } catch (error) {
      console.error('등록 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      showToast(`등록에 실패했습니다: ${errorMessage}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 전화번호 입력 핸들러 (숫자만 입력)
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setValue('phone', value);
  };

  // 성별 옵션
  const genderOptions = [
    { value: 'male', label: '남성' },
    { value: 'female', label: '여성' },
    { value: 'other', label: '기타' },
  ];

  // 연령대 옵션
  const ageGroupOptions = [
    { value: 'teen', label: '10대' },
    { value: 'twenties', label: '20대' },
    { value: 'thirties', label: '30대' },
    { value: 'forties', label: '40대' },
    { value: 'fifties', label: '50대' },
    { value: 'sixties_plus', label: '60대 이상' },
  ];

  // 지역 옵션
  const regionOptions = [
    { value: 'seoul', label: '서울' },
    { value: 'busan', label: '부산' },
    { value: 'daegu', label: '대구' },
    { value: 'incheon', label: '인천' },
    { value: 'gwangju', label: '광주' },
    { value: 'daejeon', label: '대전' },
    { value: 'ulsan', label: '울산' },
    { value: 'sejong', label: '세종' },
    { value: 'gyeonggi', label: '경기' },
    { value: 'gangwon', label: '강원' },
    { value: 'chungbuk', label: '충북' },
    { value: 'chungnam', label: '충남' },
    { value: 'jeonbuk', label: '전북' },
    { value: 'jeonnam', label: '전남' },
    { value: 'gyeongbuk', label: '경북' },
    { value: 'gyeongnam', label: '경남' },
    { value: 'jeju', label: '제주' },
    { value: 'other', label: 'etc' },
  ];

  return (
    <>
      <Helmet>
        <title>{branding.ogTitle}</title>
        <meta name="description" content={branding.ogDescription} />
        
        {/* Favicon */}
        <link rel="icon" type="image/png" href={branding.favicon} />
        <link rel="shortcut icon" type="image/png" href={branding.favicon} />
        <link rel="apple-touch-icon" href={branding.favicon} />
        
        {/* Open Graph 메타 태그 */}
        <meta property="og:title" content={branding.ogTitle} />
        <meta property="og:description" content={branding.ogDescription} />
        <meta property="og:image" content={`${window.location.origin}${branding.ogImage}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="UrbanComplex Exhibition" />
        
        {/* Twitter Card 메타 태그 */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={branding.ogTitle} />
        <meta name="twitter:description" content={branding.ogDescription} />
        <meta name="twitter:image" content={`${window.location.origin}${branding.ogImage}`} />
      </Helmet>
      
      <div 
        className="min-h-screen w-full relative"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${branding.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          backgroundColor: branding.primaryColor === 'bg-blue-600' ? '#1e40af' : '#dc2626' // fallback color
        }}
      >
      {/* 메인 콘텐츠 */}
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto bg-white/95 backdrop-blur-sm shadow-2xl">
            <CardHeader>
              {/* 로고 이미지 */}
              <div className="flex justify-center mb-1">
                <img
                  src={branding.logo}
                  alt={branding.name}
                  className="h-16 w-auto"
                  onError={(e) => {
                    console.error('로고 이미지 로드 실패:', branding.logo);
                  }}
                />
              </div>
              <CardTitle className={cn(
                "text-2xl font-bold text-center mb-4",
                branding.primaryColor === 'bg-blue-600' && "text-blue-600",
                branding.primaryColor === 'bg-red-600' && "text-red-600"
              )}>
                {branding.title}
              </CardTitle>
              <p className="text-center text-gray-600 text-sm mt-6">
                {branding.description}
              </p>
              <p className="text-center text-gray-500 text-xs mt-2 leading-relaxed">
                본 전시회의 주관사인 어반컴플렉스는 전시회 등록시스템 서비스 제공을 위하여 관계 법령에 따라 아래와 같이 개인정보를 수집, 이용하는 내용을 알리오니 자세히 읽으신 후 동의 여부를 결정하여 주십시오.<br /><br />
                <span className="text-gray-400">
                  Urban Complex, the organizer of this exhibition, collects and uses personal information as described below in accordance with relevant laws to provide exhibition registration system services. Please read carefully and decide whether to consent.
                </span>
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* 개인정보 수집 및 이용 약관 */}
                <div>
                  <div className="h-40 overflow-y-auto border border-gray-300 rounded-md p-4 bg-gray-50">
                    <PrivacyPolicy />
                  </div>
                </div>

                {/* 이름 입력 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    이름 (Name) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="이름을 입력하세요"
                    {...register('name')}
                    className={cn(errors.name && 'border-red-500')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.name)}</p>
                  )}
                </div>

                {/* 전화번호 입력 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    전화번호 (Phone) <span className="text-blue-600 font-normal">- 내국인만 등록해주세요 (For Korean nationals only)</span> <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="01012345678"
                    {...register('phone')}
                    onChange={handlePhoneChange}
                    className={cn(errors.phone && 'border-red-500')}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.phone)}</p>
                  )}
                </div>

                {/* 이메일 입력 */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    이메일 (Email) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="example@example.com"
                    {...register('email')}
                    className={cn(errors.email && 'border-red-500')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.email)}</p>
                  )}
                </div>

                {/* 국적 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    국적 (Nationality) <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('nationality')}
                    className={cn(
                      "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      errors.nationality && 'border-red-500'
                    )}
                  >
                    <option value="domestic">내국인</option>
                    <option value="foreigner">외국인 (Foreigner)</option>
                  </select>
                  {errors.nationality && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.nationality)}</p>
                  )}
                </div>

                {/* 성별 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    성별 (Gender) <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {genderOptions.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          value={option.value}
                          {...register('gender')}
                          className="mr-2"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                  {errors.gender && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.gender)}</p>
                  )}
                </div>

                {/* 연령대 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    연령대 (Age Group) <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('age_group')}
                    className={cn(
                      "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      errors.age_group && 'border-red-500',
                      branding.primaryColor === 'bg-red-600' && "focus:ring-red-500"
                    )}
                  >
                    {ageGroupOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.age_group && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.age_group)}</p>
                  )}
                </div>

                {/* 지역 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    지역 (Region) <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('region')}
                    className={cn(
                      "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      errors.region && 'border-red-500',
                      branding.primaryColor === 'bg-red-600' && "focus:ring-red-500"
                    )}
                  >
                    {regionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.region && (
                    <p className="mt-1 text-sm text-red-500">{getErrorMessage(errors.region)}</p>
                  )}
                </div>

                {/* 개인정보 동의 */}
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="privacy_consent"
                      {...register('privacy_consent')}
                      className="mr-2 mt-1"
                    />
                    <label htmlFor="privacy_consent" className="text-sm">
                      개인정보 수집 및 이용에 동의합니다<br />
                      <span className="text-gray-500">(I agree to the collection and use of personal information)</span> <span className="text-red-500">*</span>
                    </label>
                  </div>
                  {errors.privacy_consent && (
                    <p className="text-sm text-red-500">{getErrorMessage(errors.privacy_consent)}</p>
                  )}

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="third_party_consent"
                      {...register('third_party_consent')}
                      className="mr-2 mt-1"
                    />
                    <label htmlFor="third_party_consent" className="text-sm">
                      개인정보 제3자 제공 동의<br />
                      <span className="text-gray-500">(I agree to provide personal information to third parties)</span> <span className="text-red-500">*</span>
                    </label>
                  </div>
                  {errors.third_party_consent && (
                    <p className="text-sm text-red-500">{getErrorMessage(errors.third_party_consent)}</p>
                  )}

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="sms_consent"
                      {...register('sms_consent')}
                      className="mr-2 mt-1"
                    />
                    <label htmlFor="sms_consent" className="text-sm">
                      {branding.smsConsentText}
                    </label>
                  </div>
                </div>

                {/* 제출 버튼 */}
                <Button
                  type="submit"
                  className={cn(
                    "w-full",
                    branding.primaryColor === 'bg-blue-600' && "bg-blue-600 hover:bg-blue-700",
                    branding.primaryColor === 'bg-red-600' && "bg-red-600 hover:bg-red-700"
                  )}
                  disabled={isLoading}
                >
                  {isLoading ? '등록 중...' : branding.buttonText}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* 개발 환경에서만 환경변수 디버그 표시 */}
      {process.env.NODE_ENV === 'development' && <EnvDebug />}
    </div>
    </>
  );
};

export default RegistrationPage; 